import React, { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2, Upload, Loader2, Trash2, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "company_name", label: "Nome / Razão Social", placeholder: "Ex.: Hangar Aero Brasil Ltda", required: true },
  { key: "tagline", label: "Slogan / Atividade", placeholder: "Ex.: Ground Handling & Hangaragem" },
  { key: "cnpj", label: "CNPJ", placeholder: "00.000.000/0001-00" },
  { key: "address_line1", label: "Endereço (linha 1)", placeholder: "Rua / Av., número" },
  { key: "address_line2", label: "Endereço (linha 2)", placeholder: "Cidade / UF / CEP" },
  { key: "email", label: "E-mail de contato", placeholder: "contato@empresa.com.br", type: "email" },
  { key: "phone", label: "Telefone", placeholder: "(11) 0000-0000" },
  { key: "website", label: "Website", placeholder: "https://" },
];

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [wiping, setWiping] = useState(false);

  const close = () => {
    setOpen(false);
    setConfirmText("");
    setPassword("");
  };

  const wipe = async () => {
    setWiping(true);
    try {
      const { data } = await api.post("/admin/wipe-operational-data", { password });
      const total = Object.values(data.removed || {}).reduce((a, b) => a + b, 0);
      toast.success(`Dados operacionais zerados: ${total} registros removidos.`);
      close();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setWiping(false);
    }
  };

  return (
    <Card className="p-5 rounded-sm border-red-200 bg-red-50/50 mt-8">
      <div className="text-xs font-bold tracking-[0.15em] uppercase text-red-600 mb-2 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5" /> Zona de Perigo
      </div>
      <h3 className="font-display font-black text-lg tracking-tight text-slate-950 mb-1">Zerar dados operacionais</h3>
      <p className="text-sm text-slate-600 mb-4 max-w-xl">
        Apaga permanentemente <strong>clientes, aeronaves, pilotos, serviços, ordens de serviço,
        arquivos/fotos e convites</strong>, e reinicia a numeração de OS e notas.
        Usuários e as configurações do hangar são preservados. <strong>Não há como desfazer.</strong>
      </p>
      <Button
        variant="outline"
        className="rounded-sm h-11 border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600"
        onClick={() => setOpen(true)}
        data-testid="wipe-data-button"
      >
        <Trash2 className="w-4 h-4 mr-2" /> Zerar dados operacionais
      </Button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Confirmação necessária
            </DialogTitle>
            <DialogDescription>
              Esta ação apaga todos os dados operacionais de forma <strong>permanente</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">
                Digite <span className="font-mono text-red-600">ZERAR</span> para confirmar
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="ZERAR"
                className="mt-1 h-11 rounded-sm font-mono"
                data-testid="wipe-confirm-text-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Sua senha de administrador</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1 h-11 rounded-sm"
                data-testid="wipe-password-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm h-11" onClick={close} data-testid="wipe-cancel-button">
              Cancelar
            </Button>
            <Button
              className="rounded-sm h-11 bg-red-600 hover:bg-red-700 text-white"
              disabled={confirmText !== "ZERAR" || !password || wiping}
              onClick={wipe}
              data-testid="wipe-confirm-button"
            >
              {wiping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Apagar tudo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function HangarSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInput = useRef(null);

  const fetchSettings = async () => {
    const { data } = await api.get("/settings/hangar");
    setForm(data);
  };

  useEffect(() => { fetchSettings(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/hangar", form);
      setForm(data);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": undefined } });
      const next = { ...form, logo_path: data.path };
      setForm(next);
      await api.put("/settings/hangar", { logo_path: data.path });
      toast.success("Logo atualizado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no upload do logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remover o logo?")) return;
    try {
      await api.put("/settings/hangar", { logo_path: null });
      setForm((p) => ({ ...p, logo_path: null }));
      toast.success("Logo removido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  if (!form) {
    return <div className="p-8 text-slate-500">Carregando…</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Configurações</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-950">Hangar / Empresa</h1>
        <p className="text-sm text-slate-600 mt-1">Marca, CNPJ e contatos aplicados em notas e cabeçalhos.</p>
      </div>

      {/* Logo */}
      <Card className="p-5 rounded-sm border-slate-200 mb-5">
        <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-3">Logo</div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-40 h-24 border-2 border-dashed border-slate-300 rounded-sm bg-white flex items-center justify-center overflow-hidden" data-testid="logo-preview">
            {form.logo_path ? (
              <img src={`${API}/files/${form.logo_path}`} alt="Logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <div className="text-center text-slate-400">
                <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                <div className="text-[10px] uppercase tracking-wider font-bold">Sem logo</div>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm h-11"
              disabled={uploadingLogo}
              onClick={() => logoInput.current?.click()}
              data-testid="upload-logo-button"
            >
              {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {form.logo_path ? "Trocar logo" : "Enviar logo"}
            </Button>
            {form.logo_path && (
              <Button type="button" variant="outline" className="rounded-sm h-11 text-red-600 hover:text-red-700" onClick={removeLogo} data-testid="remove-logo-button">
                <Trash2 className="w-4 h-4 mr-2" /> Remover
              </Button>
            )}
            <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Recomendado: PNG transparente, ~400×150px, até 5MB.</p>
      </Card>

      {/* Fields */}
      <Card className="p-5 rounded-sm border-slate-200 mb-5">
        <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-3 flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5" /> Dados da Empresa
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === "company_name" || f.key === "address_line1" || f.key === "address_line2" ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">{f.label}{f.required && " *"}</Label>
              <Input
                type={f.type || "text"}
                value={form[f.key] || ""}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className="h-11 rounded-sm mt-1"
                data-testid={`settings-${f.key}`}
              />
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="h-12 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm px-6"
          data-testid="save-settings-button"
        >
          {saving ? "Salvando…" : "Salvar configurações"}
        </Button>
      </div>

      <DangerZone />
    </div>
  );
}
